import React from 'react';
import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import DSIconButton from '../../../design-system/DSIconButton';
import { DSTypography } from '../../../design-system/Typography';
import DSTooltip from '../../../design-system/DSTooltip';
import DSHelpOutline from '../../../design-system/DSHelpOutline';
import useKlydoFileInput from '../hooks/useKlydoFileInput';
import { UploadLoopSpeck } from './UploadLoopSpec';
import KlydoFileInputEmpty from './KlydoFileInputEmpty';
import KlydoFileInputLoading from './KlydoFileInputLoading';
import KlydoFileInputUploaded from './KlydoFileInputUploaded';
import KlydoSoundPlaybackRadio from './KlydoSoundPlaybackRadio';
import { SoundMode } from '../../../api/klydo';
import { COLLECTION_FIELDS_SPEC } from '../../../consts/specs';

type KlydoFileInputProps = {
  klydoSecureUrl: string;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  fileName: string;
  isLoadingUrl: boolean;
  soundMode?: SoundMode;
  onSoundModeChange?: (mode: SoundMode) => void;
  playOnceFrequency?: number | null;
  onPlayOnceFrequencyChange?: (frequency: number) => void;
};

const KlydoFileInput = ({
  klydoSecureUrl,
  onFileChange,
  onRemoveFile,
  fileName,
  isLoadingUrl,
  soundMode = SoundMode.OFF,
  onSoundModeChange,
  playOnceFrequency,
  onPlayOnceFrequencyChange,
}: KlydoFileInputProps) => {
  const { spacing, palette } = useTheme();

  const { handleFileChange, handleRemoveFile } = useKlydoFileInput({
    onFileChange,
    onRemoveFile,
  });

  const renderFileInputState = () => {
    if (isLoadingUrl) {
      return <KlydoFileInputLoading />;
    }

    if (klydoSecureUrl) {
      return (
        <KlydoFileInputUploaded
          klydoSecureUrl={klydoSecureUrl}
          fileName={fileName}
          onRemoveFile={handleRemoveFile}
          onFileChange={handleFileChange}
        />
      );
    }

    return <KlydoFileInputEmpty onFileChange={handleFileChange} />;
  };

  return (
    <DSBox
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(1),
      }}
    >
      <DSBox
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <DSTypography variant="label">*Choose File</DSTypography>
        <DSTooltip title={<UploadLoopSpeck />}>
          <DSIconButton
            sx={{
              height: spacing(1),
              '&:hover': {
                backgroundColor: 'transparent',
                color: palette.text.primary,
              },
              color: palette.text.secondary,
              transition: 'color 0.3s ease',
            }}
          >
            <DSHelpOutline />
          </DSIconButton>
        </DSTooltip>
      </DSBox>
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(1),
        }}
      >
        {renderFileInputState()}

        {klydoSecureUrl && onSoundModeChange && onPlayOnceFrequencyChange && (
          <DSBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing(0.5),
            }}
          >
            <DSBox
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: spacing(0.5),
              }}
            >
              <DSTypography variant="body2" color={palette.text.secondary}>
                Background Audio
              </DSTypography>
              <DSTooltip title={COLLECTION_FIELDS_SPEC}>
                <DSIconButton
                  sx={{
                    height: spacing(1),
                    padding: 0,
                    '&:hover': {
                      backgroundColor: 'transparent',
                      color: palette.text.primary,
                    },
                    color: palette.text.secondary,
                    transition: 'color 0.3s ease',
                  }}
                >
                  <DSHelpOutline sx={{ fontSize: 16 }} />
                </DSIconButton>
              </DSTooltip>
            </DSBox>
            <DSBox sx={{ paddingLeft: spacing(2) }}>
              <KlydoSoundPlaybackRadio
                soundMode={soundMode}
                playOnceFrequency={playOnceFrequency ?? null}
                onSoundModeChange={onSoundModeChange}
                onPlayOnceFrequencyChange={onPlayOnceFrequencyChange}
              />
            </DSBox>
          </DSBox>
        )}
      </DSBox>
    </DSBox>
  );
};

export default KlydoFileInput;
